import React, { useState, useMemo } from 'react'
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Tooltip,
  Button,
  CircularProgress,
  Alert,
} from '@material-ui/core'
import {
  GridOn as VariantsIcon,
  Assignment as StandardIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  FileDownloadOutlined as ReportIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import AnalysisStatus from './AnalysisStatus'
import { useSampleMetadata } from 'src/api/vcf'
import { checkReport, downloadReport } from 'src/api/variant'
import { useCarrierAnalysisFileStatus } from 'src/api/carrierAnalysis'
import ResultButton from '../FileUploadView/components/ResultButton'
import DnaIcon from './sampleTools/DnaIcon'
import TonalChip, { getFileTypeTone } from '../FileUploadView/components/TonalChip'

const CarrierAnalysisPopover = ({ analysisRow }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Use the API hook with conditional fetching
  const { query: fileStatusQuery } = useCarrierAnalysisFileStatus(
    analysisRow.analysis_id,
    isPopoverOpen, // Only fetch when popover is open
  )

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
    setIsPopoverOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsPopoverOpen(false)
  }

  const open = Boolean(anchorEl)

  // Extract data from query
  const fileStatusData = fileStatusQuery.data?.data
  const isLoading = fileStatusQuery.isLoading
  const error = fileStatusQuery.error

  if (!analysisRow || analysisRow.analysis_type !== 'CARRIER') {
    return null
  }
  const annotations = analysisRow.analysis_parameters?.annotation || []

  const getFileIcon = (fileType) => {
    // Check if it's a VCF file
    if (fileType === 'VCF') {
      return <VariantsIcon fontSize="small" />
    }
    // Check if it's a FASTQ file
    if (fileType === 'FASTQ') {
      return <StandardIcon fontSize="small" />
    }
    return <InfoIcon fontSize="small" />
  }

  const SampleReportIcon = ({ fileId, typeParam, isDone, sampleName }) => {
    const [hasReport, setHasReport] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)

    React.useEffect(() => {
      let cancelled = false
      if (!isDone || !fileId) return
      checkReport(fileId, typeParam)
        .then((res) => {
          if (!cancelled) setHasReport(res?.status === 200)
        })
        .catch(() => {
          if (!cancelled) setHasReport(false)
        })
      return () => {
        cancelled = true
      }
    }, [fileId, typeParam, isDone])

    if (!isDone || !hasReport) return null

    const handleDownload = async (e) => {
      e.stopPropagation()
      try {
        setIsDownloading(true)
        const response = await downloadReport(fileId)
        const safeSampleName = (sampleName || 'analysis_report').toString().replace(/[^A-Za-z0-9._-]/g, '_')
        const filename = `${safeSampleName}_report.docx`
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      } catch (err) {
        console.error('Failed to download report', err)
      } finally {
        setIsDownloading(false)
      }
    }

    return (
      <Tooltip title={isDownloading ? 'Downloading…' : 'Download report (.docx)'} arrow>
        <ResultButton
          onClick={handleDownload}
          startIcon={isDownloading ? <CircularProgress size={14} color="inherit" /> : <ReportIcon />}
        >
          Report
        </ResultButton>
      </Tooltip>
    )
  }

  // Component for rendering analysis button for a specific file
  const AnalysisButton = ({ sampleName, fileId }) => {
    const sampleMetadata = useSampleMetadata({ fileId: fileId, sampleName: sampleName })
    const tables = useMemo(() => sampleMetadata.query.data?.tables ?? [], [sampleMetadata.query.data])
    const firstTableId = tables[0]?.id

    const url = `/libra/variants/${fileId}/${sampleName}/${firstTableId}`

    const handleClick = (e) => {
      // Prevent default only for left click to allow middle click to work normally
      if (e.button === 0) {
        e.preventDefault()
        window.location.href = url
      }
    }

    const handleContextMenu = (e) => {
      e.preventDefault()
      navigator.clipboard.writeText(window.location.origin + url)
    }

    const isDone = analysisRow.status === 'DONE'

    if (!isDone || !firstTableId) {
      return null
    }

    return (
      <Tooltip title="Germline SNV/Indel table" arrow>
        <ResultButton
          primary
          startIcon={<DnaIcon />}
          href={url}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
        >
          SNV/Indel
        </ResultButton>
      </Tooltip>
    )
  }

  return (
    <>
      <Tooltip title="Per-sample variants and reports" arrow>
        <ResultButton onClick={handleClick} endIcon={<ExpandMoreIcon />} sx={{ '& .MuiButton-endIcon': { ml: 0.25 } }}>
          Samples
        </ResultButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            minWidth: 350,
            maxWidth: 500,
          },
        }}
      >
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Carrier Analysis: {fileStatusData?.analysis_name || analysisRow.analysis_name}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {fileStatusData?.sample_name?.split(', ').length || analysisRow?.sample_name?.split(', ').length} sample(s)
            • {annotations.length} annotation(s)
          </Typography>

          <Divider style={{ margin: '12px 0' }} />

          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" style={{ padding: '20px' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                Loading file status...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" style={{ margin: '12px 0' }}>
              {error.message || 'Failed to load file status'}
            </Alert>
          )}

          {!isLoading && !error && fileStatusData && (
            <>
              {fileStatusData.carrier_analyses.map((carrierAnalysis, caIndex) => (
                <Box key={caIndex} style={{ marginBottom: 16 }}>
                  <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                    {carrierAnalysis.panel_title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    {carrierAnalysis.samples_required} samples required • {carrierAnalysis.finished_subanalysis_count}{' '}
                    finished
                  </Typography>

                  <List dense>
                    {carrierAnalysis.files.map((file, fileIndex) => {
                      const sharedStatus = fileStatusData.shared_status
                      const isDone = sharedStatus === 'DONE'
                      const fileType = file.type.toUpperCase()

                      return (
                        <ListItem key={fileIndex} button disabled={!isDone}>
                          <ListItemIcon>{getFileIcon(fileType)}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  {file.sample_name}
                                </Typography>
                                <TonalChip tone={getFileTypeTone(fileType)} label={fileType} />
                                {file.is_annotated && <TonalChip tone="neutral" label="Annotated" />}
                              </Box>
                            }
                            secondary={
                              <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 4 }}>
                                <Typography variant="caption" color="textSecondary">
                                  {file.type === 'fastq'
                                    ? `${file.file_1}${file.file_2 ? `, ${file.file_2}` : ''}`
                                    : file.original_name}
                                </Typography>
                                <AnalysisStatus status={sharedStatus} />
                              </Box>
                            }
                          />

                          {isDone && (
                            <Box display="flex" style={{ gap: 4 }}>
                              <AnalysisButton sampleName={file.sample_name} fileId={file.id} />
                              <SampleReportIcon
                                fileId={file.id}
                                typeParam={file.type}
                                isDone={isDone}
                                sampleName={file.sample_name}
                              />
                            </Box>
                          )}
                        </ListItem>
                      )
                    })}
                  </List>
                </Box>
              ))}

              <Divider style={{ margin: '12px 0' }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  Overall Status:
                </Typography>
                <AnalysisStatus status={fileStatusData.shared_status} />
              </Box>

              {/* Additional action buttons */}
              {(fileStatusData.shared_status === 'DONE' || analysisRow.status === 'DONE') && (
                <Box display="flex" justifyContent="center" style={{ marginTop: 16, gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/libra/carrier-report/${analysisRow.analysis_id}`}
                    onClick={handleClose}
                    startIcon={<OpenInNewIcon />}
                  >
                    View Carrier Report
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* Fallback to original display if no file status data */}
          {!isLoading && !error && !fileStatusData && (
            <List dense>
              {analysisRow?.sample_name?.split(', ').map((sample, index) => {
                const status = analysisRow.status
                const isDone = analysisRow.status === 'DONE'
                const annotation = annotations[index]

                return (
                  <ListItem key={index} button disabled={!isDone}>
                    <ListItemIcon>{getFileIcon(analysisRow.analysis_type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {sample}
                          </Typography>
                          <TonalChip
                            tone={getFileTypeTone(
                              analysisRow.file_names.find((file) => file.includes(sample))?.includes('vcf')
                                ? 'VCF'
                                : 'FASTQ',
                            )}
                            label={
                              analysisRow.file_names.find((file) => file.includes(sample))?.includes('vcf')
                                ? 'VCF'
                                : 'FASTQ'
                            }
                          />
                          {annotation && <TonalChip tone="neutral" label={annotation.role || 'Unknown role'} />}
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 4 }}>
                          <Typography variant="caption" color="textSecondary">
                            {'Unknown file'}
                          </Typography>
                          <AnalysisStatus
                            analysis={{
                              status: analysisRow.status,
                              list: [],
                            }}
                          />
                        </Box>
                      }
                    />

                    {isDone && (
                      <Box display="flex" style={{ gap: 4 }}>
                        <AnalysisButton sampleName={analysisRow.sample_name} fileId={analysisRow.file_id} />
                        <SampleReportIcon
                          fileId={analysisRow.file_id}
                          typeParam={
                            analysisRow.file_names.find((file) => file.includes(sample))?.includes('vcf')
                              ? 'vcf'
                              : 'fastq'
                          }
                          isDone={isDone}
                          sampleName={sample}
                        />
                      </Box>
                    )}
                  </ListItem>
                )
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default CarrierAnalysisPopover
